/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
  IProductDimensionsWithAvailabilitiesFull,
  ProductDimensionFull,
} from "@msdyn365-commerce/commerce-entities";
import {
  CacheType,
  createObservableDataAction,
  IAction,
  IActionContext,
  IActionInput,
  IAny,
  ICreateActionContext,
  IGeneric,
  IRequestContext,
} from "@msdyn365-commerce/core";
import {
  ProductDimension,
  ProductDimensionAvailabilitySearchCriteria,
  ProductDimensionValue,
  ProductDimensionValueInventoryAvailability,
  ProductsDataActions,
} from "@msdyn365-commerce/retail-proxy";

import {
  ArrayExtensions,
  createInventoryAvailabilitySearchCriteria,
  QueryResultSettingsProxy,
  getDimensionValuesFromQuery,
  getSelectedProductIdFromActionInput,
} from "@msdyn365-commerce-modules/retail-actions";

/**
 * Defines the dimension value type for get dimensions for selected variant data action.
 * Includes old API format for backward compatibility and new API info for availabilities for new logic.
 */
export interface IDimensionValueForSelectedVariant
  extends ProductDimensionValueInventoryAvailability,
    ProductDimensionValue {}

/**
 * Defines the returned type for get dimensions for selected variant data action.
 * Includes old API format for backward compatibility and new API info for availabilities for new logic.
 */
export interface IDimensionForSelectedVariant
  extends ProductDimensionFull,
    IProductDimensionsWithAvailabilitiesFull {}

/**
 * Input class for the getDimensionsForSelectedVariant data action.
 */
export class GetDimensionsForSelectedVariantInput implements IActionInput {
  public productId: number;

  public channelId: number;

  public matchingDimensionValues: ProductDimension[];

  public catalogId?: number;

  public constructor(
    productId: number,
    channelId: number,
    matchingDimensionValues?: ProductDimension[],
    requestContext?: IRequestContext
  ) {
    this.productId = productId;
    this.channelId = channelId;
    this.matchingDimensionValues = matchingDimensionValues || [];

    if (requestContext) {
      //this.catalogId = getCatalogId(requestContext);
      this.catalogId = Number(requestContext.user.catalogId);
    }
  }

  /**
   * Cache key.
   * @returns String.
   */
  public getCacheKey: () => string = () =>
    `DimensionsForSelectedVariantInput-${this.productId}-${this.channelId}-${
      this.catalogId ?? 0
    }`;

  /**
   * Cache object type.
   * @returns String.
   */
  public getCacheObjectType: () => string = () =>
    "DimensionsForSelectedVariantInput";

  /**
   * Cache type.
   * @returns String.
   */
  public dataCacheType = (): CacheType => "none";
}

/**
 * Create input method for the getDimensionsForSelectedVariant data action.
 * @param inputData - The input data passed to the createInput method.
 * @returns - The input for the data action.
 */
const createDimensionsForSelectedVariantInput = (
  inputData: ICreateActionContext<IGeneric<IAny>>
): GetDimensionsForSelectedVariantInput => {
  const productId = getSelectedProductIdFromActionInput(inputData);

  if (productId) {
    return new GetDimensionsForSelectedVariantInput(
      +productId,
      +inputData.requestContext.apiSettings.channelId,
      getDimensionValuesFromQuery(inputData.requestContext.url.requestUrl),
      inputData.requestContext
    );
  }
  throw new Error(
    "Unable to create DimensionsForSelectedVariantInput, no productId found on module config or query"
  );
};

/**
 * Converts new API type with availabilities to old API type to preserve backward compatibility.
 * @param dimensions - Dimensions to convert.
 * @returns Dimensions in old API format.
 */
const prepareDimensionsDataResult = (
  dimensions: IProductDimensionsWithAvailabilitiesFull[]
): IDimensionForSelectedVariant[] => {
  return dimensions.map((newDimension) => {
    const oldDimension: IDimensionForSelectedVariant = { ...newDimension };
    oldDimension.DimensionValues = ArrayExtensions.validValues(
      newDimension.dimensionValuesWithInventory?.map(
        (value) => value.DimensionValue
      )
    );
    return oldDimension;
  });
};

/**
 * Action Method for the getDimensionsForSelectedVariant Data Action.
 * @param input - Input for the data action.
 * @param context - Action context.
 * @returns Product dimensions with availability.
 */
export async function getDimensionsForSelectedVariantAction(
  input: GetDimensionsForSelectedVariantInput,
  context: IActionContext
): Promise<IDimensionForSelectedVariant[]> {
  const activeProducts = ArrayExtensions.validValues(
    await ProductsDataActions.getByIdsAsync(
      {
        callerContext: context,
        queryResultSettings:
          QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(
            context
          ),
      },
      input.channelId,
      [input.productId],
      null,
      Number(context.requestContext.user.catalogId)
    )
  );

  if (!ArrayExtensions.hasElements(activeProducts)) {
    return [];
  }

  const activeProduct = activeProducts[0];

  if (!ArrayExtensions.hasElements(activeProduct.Dimensions)) {
    return [];
  }

  const promises = activeProduct.Dimensions.map((dimension) => {
    const shippingInventoryConfiguration =
      createInventoryAvailabilitySearchCriteria(
        context,
        [],
        true,
        undefined,
        undefined
      );

    const searchCriteria: ProductDimensionAvailabilitySearchCriteria = {
      RequestedDimensionTypeValue: dimension.DimensionTypeValue,
      MatchingDimensionValues: input.matchingDimensionValues.filter(
        (item) => item.DimensionTypeValue !== dimension.DimensionTypeValue
      ),
      DefaultWarehouseOnly: shippingInventoryConfiguration.DefaultWarehouseOnly,
      FilterByChannelFulfillmentGroup:
        shippingInventoryConfiguration.FilterByChannelFulfillmentGroup,
      DeliveryModeTypeFilterValue:
        shippingInventoryConfiguration.DeliveryModeTypeFilterValue,
      CatalogId: Number(context.requestContext.user.catalogId),
    };

    const dimensionValuesPromise =
      ProductsDataActions.getDimensionValuesWithEstimatedAvailabilitiesAsync(
        {
          callerContext: context,
          queryResultSettings:
            QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(
              context
            ),
        },
        activeProduct.MasterProductId
          ? activeProduct.MasterProductId
          : activeProduct.RecordId,
        searchCriteria
      );
    const fullDimensionPromise = dimensionValuesPromise.then(
      (dimensionValues) => {
        const fullDimension: IProductDimensionsWithAvailabilitiesFull = {
          ...dimension,
          dimensionValuesWithInventory: dimensionValues,
        };
        return fullDimension;
      }
    );

    return fullDimensionPromise;
  });

  const dimensionValuesResults = await Promise.all(promises);

  return prepareDimensionsDataResult(dimensionValuesResults);
}

/**
 * The GetDimensionsForSelectedVariantDataAction
 * Get the currently selected Variant via the getSelectedVariant data action, and the gets the
 * available product dimensions for the variant via the getDimensionValues RetailServer API.
 */
export const getDimensionsForSelectedVariantActionDataAction =
  createObservableDataAction({
    id: "@msdyn365-commerce-modules/retail-actions/get-dimensions/for-selected-variant",
    action: getDimensionsForSelectedVariantAction as IAction<
      IDimensionForSelectedVariant[]
    >,
    input: createDimensionsForSelectedVariantInput,
  });

export default getDimensionsForSelectedVariantActionDataAction;
